/**
 * ActiveFiltersHeader - Shows active filters as removable chips above grid/table
 * Corporate UX with glassmorphism design
 */

import { h } from 'preact'
import {
  selectedChemistries,
  selectedCathodeChemistries,
  selectedAnodeChemistries,
  selectedManufacturers,
  selectedCountries,
  selectedFormFactors,
  selectedTechnologies,
  energyRange,
  powerRange,
  capacityRange,
  yearRange,
  batchVariationRange,
  agingRange,
  fullRangeEnergy,
  fullRangePower,
  fullRangeYear,
  fullRangeBatchVariation,
  fullRangeAging,
  searchQuery,
  savedSearchTerms,
  hasActiveFilters,
  resetAllFilters,
  toggleFilter,
  updateRange,
  ionCellIdSet,
  ionCellIdSetLabel,
  clearIonCellSet,
} from '../../state/filterStore.js'
import { basis, domain, filteredCells } from '../../state/globalSignals.js'
import { commandProvenance, revertIonTransaction } from '../../state/commandLayer.js'

// Helper to check if range is modified
const isRangeModified = (range, fullRange) => {
  return range.min !== fullRange.min || range.max !== fullRange.max
}

export default function ActiveFiltersHeader() {
  const activeFilters = []
  
  // Cathode Chemistry filters (NEW)
  selectedCathodeChemistries.value.forEach(chem => {
    activeFilters.push({
      type: 'cathode-chemistry',
      label: `Cathode: ${chem}`,
      onRemove: () => toggleFilter('cathode-chemistry', chem)
    })
  })
  
  // Anode Chemistry filters (NEW)
  selectedAnodeChemistries.value.forEach(chem => {
    activeFilters.push({
      type: 'anode-chemistry',
      label: `Anode: ${chem}`,
      onRemove: () => toggleFilter('anode-chemistry', chem)
    })
  })
  
  // Legacy Chemistry filters
  selectedChemistries.value.forEach(chem => {
    activeFilters.push({
      type: 'chemistry',
      label: chem,
      onRemove: () => toggleFilter('chemistry', chem)
    })
  })
  
  // Manufacturer filters
  selectedManufacturers.value.forEach(mfr => {
    activeFilters.push({
      type: 'manufacturer',
      label: mfr,
      onRemove: () => toggleFilter('manufacturer', mfr)
    })
  })
  
  // Country filters
  selectedCountries.value.forEach(country => {
    activeFilters.push({
      type: 'country',
      label: country,
      onRemove: () => toggleFilter('country', country)
    })
  })
  
  // Form Factor filters
  selectedFormFactors.value.forEach(format => {
    activeFilters.push({
      type: 'form-factor',
      label: format,
      onRemove: () => toggleFilter('form-factor', format)
    })
  })
  
  // Technology filters
  selectedTechnologies.value.forEach(tech => {
    activeFilters.push({
      type: 'technology',
      label: tech,
      onRemove: () => toggleFilter('technology', tech)
    })
  })
  
  // Current search query (live search)
  const currentSearch = (searchQuery.value || '').trim()
  if (currentSearch) {
    activeFilters.push({
      type: 'search',
      label: `"${currentSearch}"`,
      onRemove: () => {
        searchQuery.value = ''
      }
    })
  }
  
  // Saved search terms
  savedSearchTerms.value.forEach((term, idx) => {
    activeFilters.push({
      type: 'search',
      label: `"${term}"`,
      onRemove: () => {
        savedSearchTerms.value = savedSearchTerms.value.filter((_, i) => i !== idx)
      }
    })
  })
  
  // Range filters - only show if different from full range
  const isEnergyModified = energyRange.value.min !== fullRangeEnergy.value.min || 
                          energyRange.value.max !== fullRangeEnergy.value.max
  const isPowerModified = powerRange.value.min !== fullRangePower.value.min || 
                         powerRange.value.max !== fullRangePower.value.max
  const isYearModified = yearRange.value.min !== fullRangeYear.value.min || 
                        yearRange.value.max !== fullRangeYear.value.max
  
  if (isEnergyModified) {
    const unit = basis.value === 'grav' ? 'Wh/kg' : basis.value === 'vol' ? 'Wh/L' : 'Wh'
    activeFilters.push({
      type: 'range',
      label: `Energy: ${Math.round(energyRange.value.min)}-${Math.round(energyRange.value.max)} ${unit}`,
      onRemove: () => updateRange('energy', { ...fullRangeEnergy.value })
    })
  }
  
  if (isPowerModified) {
    const unit = basis.value === 'grav' ? 'W/kg' : basis.value === 'vol' ? 'W/L' : 'W'
    activeFilters.push({
      type: 'range',
      label: `Power: ${Math.round(powerRange.value.min)}-${Math.round(powerRange.value.max)} ${unit}`,
      onRemove: () => updateRange('power', { ...fullRangePower.value })
    })
  }
  
  if (isYearModified) {
    activeFilters.push({
      type: 'range',
      label: `Year: ${yearRange.value.min}-${yearRange.value.max}`,
      onRemove: () => updateRange('year', { ...fullRangeYear.value })
    })
  }
  
  // Batch Variation Range
  const isBatchModified = batchVariationRange.value.min !== fullRangeBatchVariation.value.min || 
                         batchVariationRange.value.max !== fullRangeBatchVariation.value.max
  if (isBatchModified) {
    activeFilters.push({
      type: 'range',
      label: `Batch Var: ${batchVariationRange.value.min.toFixed(1)}-${batchVariationRange.value.max.toFixed(1)}%`,
      onRemove: () => updateRange('batchVariation', { ...fullRangeBatchVariation.value })
    })
  }
  
  // Aging Range
  const isAgingModified = agingRange.value.min !== fullRangeAging.value.min || 
                         agingRange.value.max !== fullRangeAging.value.max
  if (isAgingModified) {
    activeFilters.push({
      type: 'range',
      label: `Post-Formation Loss: ${agingRange.value.min.toFixed(1)}-${agingRange.value.max.toFixed(1)}%`,
      onRemove: () => updateRange('aging', { ...fullRangeAging.value })
    })
  }
  
  // Ion's explicit id set (D4) — LAST in the chip row so it reads as the outer
  // bracket around the property filters above it, which is what it is.
  //
  // Marked `ion-cell-set` rather than folded in with the others: it is the one
  // chip whose meaning cannot be read off the grid. "Cathode: NMC" is verifiable
  // by looking at the cells; "the 5 most recently added" is a claim the user has
  // to take on trust, so it says who made it. Removable, not editable — editing
  // a list of opaque 16-hex ids is not a thing anyone wants to do by hand.
  if (ionCellIdSet.value) {
    const count = ionCellIdSet.value.size
    activeFilters.push({
      type: 'ion-cell-set',
      label: ionCellIdSetLabel.value
        ? `✦ ${ionCellIdSetLabel.value}`
        : `✦ ${count} cell${count === 1 ? '' : 's'} selected by Ion`,
      onRemove: () => clearIonCellSet(),
    })
  }

  // Ion attribution — only a COMMITTED turn carries a label (commitIonTransaction,
  // state/commandLayer.js); a still-open or just-reverted transaction doesn't, so
  // checking for `.label` also keeps the card from flashing mid-turn or reappearing
  // right after Revert. The provenance-degradation effect in commandLayer.js nulls
  // commandProvenance the moment the user makes any manual filter/sort/view change.
  const provenance = commandProvenance.value
  const hasAttribution = !!provenance?.label

  // Don't show header if no active filters and no Ion attribution to show
  const hasSearch = (searchQuery.value || '').trim() !== ''
  const hasSavedTerms = savedSearchTerms.value.length > 0
  // `hasActiveFilters` is filterStore's own computed and now counts the id set
  // too, but check the signal directly as well: the header must never be the
  // thing that hides the one chip explaining why the grid shrank.
  if (
    !hasActiveFilters.value &&
    !hasSearch &&
    !hasSavedTerms &&
    !hasAttribution &&
    !ionCellIdSet.value
  ) {
    return null
  }

  return (
    <div className="active-filters-header">
      {activeFilters.length > 0 && (
        <div className="active-filters-label">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
          </svg>
          <span>Active Filters ({activeFilters.length})</span>
        </div>
      )}

      {hasAttribution && (
        <div className="ion-attribution-card">
          <span className="ion-attribution-label">
            Ion: {provenance.label} · {filteredCells.value.length} cells
          </span>
          <button
            className="ion-attribution-revert"
            onClick={() => revertIonTransaction()}
            title="Undo Ion's change"
          >
            Revert
          </button>
        </div>
      )}

      {activeFilters.length > 0 && (
        <div className="active-filters-chips">
          {activeFilters.map((filter, idx) => (
            <div key={idx} className={`filter-chip filter-chip-${filter.type}`}>
              <span className="filter-chip-label">{filter.label}</span>
              <button
                className="filter-chip-remove"
                onClick={filter.onRemove}
                title="Remove filter"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {activeFilters.length > 1 && (
        <button
          className="clear-all-filters"
          onClick={resetAllFilters}
          title="Clear all filters"
        >
          Clear All
        </button>
      )}
    </div>
  )
}
